import {debounce} from 'lodash';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';

import {apolloClient} from '../../../apolloClient';
import {
  useViewerTeamsProjectsQuery,
  ViewerTeamsProjectsDocument,
  ViewerTeamsProjectsQuery,
  ViewerTeamsProjectsQueryVariables,
} from '../../../generated/graphql';
import {FETCH_PROJECT_RUNS_FOR_CONFIG} from '../../../state/graphql/launch';
import {useViewer} from '../../../state/viewer/hooks';
import {JobDetails, JobRun, JobVersion} from '../JobDetail/utilTypes';
import {getDefaultColor} from '../utils';

export const useJobDrawerConfigImporter = (options?: {
  job?: JobDetails | null;
  defaultConfigSourceRunName?: string;
}) => {
  const viewer = useViewer();
  const {job, defaultConfigSourceRunName} = options ?? {};
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const [configSourceRun, setConfigSourceRun] = useState<JobRun | null>(null);

  const previousRuns = useMemo(() => {
    return job?.runs.filter(
      run => run.version === job.versionInfo.runTargetNum
    );
  }, [job?.runs, job?.versionInfo.runTargetNum]);

  const defaultConfigSourceRun = useMemo(
    () =>
      getDefaultConfigSourceRun(
        previousRuns ?? [],
        defaultConfigSourceRunName,
        projectName,
        entityName,
        viewer?.username
      ),
    [
      previousRuns,
      defaultConfigSourceRunName,
      projectName,
      entityName,
      viewer?.username,
    ]
  );

  return {
    configSourceRun,
    setConfigSourceRun,
    defaultConfigSourceRun,
    job,
  };
};

const getDefaultConfigSourceRun = (
  previousRuns: JobRun[],
  defaultConfigSourceRunName?: string,
  projectName?: string,
  entityName?: string,
  viewerUsername?: string
): JobRun | null => {
  if (!previousRuns.length) {
    return null;
  }

  // make sure the default run is from the current project
  if (defaultConfigSourceRunName && projectName && entityName) {
    const namedRun = previousRuns.find(
      run => run.name === defaultConfigSourceRunName
    );
    if (
      namedRun &&
      namedRun.projectName === projectName &&
      namedRun.entityName === entityName
    ) {
      return namedRun;
    }
  }

  const projectRuns =
    projectName && entityName
      ? previousRuns.filter(
          run =>
            run.projectName === projectName && run.entityName === entityName
        )
      : previousRuns;

  if (!projectRuns.length) {
    return null;
  }

  const userRuns = viewerUsername
    ? projectRuns.filter(run => run.creator?.username === viewerUsername)
    : [];

  const runsToConsider = userRuns.length > 0 ? userRuns : projectRuns;

  // prefer most recent run as best starting point for new job launches
  return runsToConsider.reduce((latest, run) =>
    new Date(run.createdAt) > new Date(latest.createdAt) ? run : latest
  );
};

export type ProjectOption = {
  value: string;
  entityName: string;
  projectName: string;
};

export type RunOption = {
  value: string;
  run: JobRun;
  label: string;
};

interface UseConfigImporterSelectionProps {
  configSourceRun: JobRun | null;
  defaultConfigSourceRun: JobRun | null;
  job?: JobDetails | null;
}

interface UseConfigImporterSelectionReturn {
  projectOptions: ProjectOption[];
  runOptions: RunOption[];
  selectedProjectOption: ProjectOption | null;
  selectedRunOption: RunOption | null;
  selectedConfigSourceRun: JobRun | null;
  loadingRuns: boolean;
  onSelectProject: (project: ProjectOption | null) => void;
  onSelectRun: (run: RunOption | null) => void;
  loadProjectOptions: (
    inputValue: string,
    callback: (options: ProjectOption[]) => void
  ) => void;
}

export const useConfigImporterSelection = ({
  configSourceRun,
  defaultConfigSourceRun,
  job,
}: UseConfigImporterSelectionProps): UseConfigImporterSelectionReturn => {
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();
  const viewer = useViewer();

  const [selectedConfigSourceRun, setSelectedConfigSourceRun] =
    useState<JobRun | null>(configSourceRun || defaultConfigSourceRun);
  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(
    null
  );
  const [runOptions, setRunOptions] = useState<RunOption[]>([]);
  const [loadingRuns, setLoadingRuns] = useState(false);

  const {data: defaultProjectsData} = useViewerTeamsProjectsQuery();

  const projectOptions = useMemo(() => {
    const options: ProjectOption[] = [];

    if (defaultProjectsData?.viewer?.teams?.edges) {
      defaultProjectsData.viewer.teams.edges.forEach(teamEdge => {
        const team = teamEdge?.node;
        if (!team?.projects?.edges) {
          return;
        }

        team.projects.edges.forEach(projectEdge => {
          const project = projectEdge?.node;
          if (!project) {
            return;
          }

          options.push({
            value: `${team.name}/${project.name}`,
            entityName: team.name,
            projectName: project.name,
          });
        });
      });
    }

    options.sort((a, b) => a.value.localeCompare(b.value));

    // ensure current project is always available, even if not in top 100 per team
    if (entityName && projectName) {
      const currentProjectKey = `${entityName}/${projectName}`;
      const currentProjectExists = options.some(
        option => option.value === currentProjectKey
      );
      if (!currentProjectExists) {
        options.unshift({
          value: currentProjectKey,
          entityName,
          projectName,
        });
      }
    }

    return options;
  }, [defaultProjectsData, entityName, projectName]);

  const selectedProjectOption = useMemo(() => {
    if (selectedProject) {
      return selectedProject;
    }

    if (selectedConfigSourceRun) {
      const key = `${selectedConfigSourceRun.entityName}/${selectedConfigSourceRun.projectName}`;
      return projectOptions.find(option => option.value === key) ?? null;
    }

    return null;
  }, [selectedProject, selectedConfigSourceRun, projectOptions]);

  const selectedRunOption = useMemo(() => {
    if (!selectedConfigSourceRun) {
      return null;
    }
    return (
      runOptions.find(
        option => option.value === selectedConfigSourceRun.name
      ) ?? null
    );
  }, [selectedConfigSourceRun, runOptions]);

  // preserve debounced function across renders to maintain its internal timer state
  const debouncedSearchProjects = useRef(
    debounce(
      (inputValue: string, callback: (options: ProjectOption[]) => void) => {
        apolloClient
          .query<ViewerTeamsProjectsQuery, ViewerTeamsProjectsQueryVariables>({
            query: ViewerTeamsProjectsDocument,
            variables: {pattern: inputValue},
            fetchPolicy: 'network-only',
          })
          .then(result => {
            if (result.data?.viewer?.teams?.edges) {
              const searchResults: ProjectOption[] = [];

              result.data.viewer.teams.edges.forEach(teamEdge => {
                const team = teamEdge?.node;
                if (!team?.projects?.edges) {
                  return;
                }

                team.projects.edges.forEach(projectEdge => {
                  const project = projectEdge?.node;
                  if (!project) {
                    return;
                  }

                  searchResults.push({
                    value: `${team.name}/${project.name}`,
                    entityName: team.name,
                    projectName: project.name,
                  });
                });
              });

              searchResults.sort((a, b) => a.value.localeCompare(b.value));

              callback(searchResults);
            } else {
              callback([]);
            }
          })
          .catch((error: Error) => {
            console.error('Failed to search projects:', error);
            callback([]);
          });
      },
      300
    )
  ).current;

  const loadProjectOptions = useCallback(
    (inputValue: string, callback: (options: ProjectOption[]) => void) => {
      if (!inputValue) {
        callback(projectOptions);
        return;
      }

      debouncedSearchProjects(inputValue, callback);
    },
    [projectOptions, debouncedSearchProjects]
  );

  const onSelectProject = useCallback(
    async (selectedProject: ProjectOption | null) => {
      if (!selectedProject) {
        return;
      }
      setSelectedProject(selectedProject);

      // only clear selected run if switching to a different project
      if (selectedConfigSourceRun) {
        const currentProjectKey = `${selectedConfigSourceRun.entityName}/${selectedConfigSourceRun.projectName}`;
        if (currentProjectKey !== selectedProject.value) {
          setSelectedConfigSourceRun(null);
        }
      }

      setLoadingRuns(true);
      setRunOptions([]);

      try {
        const result = await apolloClient.query({
          query: FETCH_PROJECT_RUNS_FOR_CONFIG,
          variables: {
            entityName: selectedProject.entityName,
            projectName: selectedProject.projectName,
            first: 50,
          },
          fetchPolicy: 'network-only',
        });

        const runs =
          result.data?.project?.runs?.edges
            ?.filter(
              (edge: any) => edge?.node?.job?.artifactSequence?.id === job?.id
            )
            .map((edge: any) => {
              const node = edge?.node;

              const jobRun: JobRun = {
                id: node.id,
                name: node.name,
                displayName: node.displayName || node.name,
                createdAt: node.createdAt,
                state: node.state,
                defaultColor: getDefaultColor(
                  node.defaultColorIndex,
                  node.name
                ),
                creator: node.user
                  ? {
                      id: node.user.id,
                      name: node.user.name || node.user.username,
                      username: node.user.username,
                      photoUrl: node.user.photoUrl || '',
                    }
                  : null,
                version: node.job.versionIndex || 0,
                jobAliases: node.job.aliases || [],
                config: (() => {
                  try {
                    if (typeof node.config === 'string') {
                      return JSON.parse(node.config);
                    }
                    return (node.config as Record<string, any>) || {};
                  } catch (error) {
                    console.warn('Failed to parse config JSON:', error);
                    return {};
                  }
                })(),
                args: node.runInfo?.args || [],
                entityName: selectedProject.entityName,
                projectName: selectedProject.projectName,
              };

              return jobRun;
            })
            .filter((run: JobRun | null) => {
              if (!run) {
                return false;
              }

              // only show runs from same job and version
              if (job) {
                if (run.jobAliases.length === 0) {
                  return false;
                }

                const currentJobAliasesSet = new Set(
                  job.versions?.flatMap((v: JobVersion) =>
                    v.aliases.map(a => a.alias)
                  ) || []
                );

                const hasMatchingJobAlias = run.jobAliases.some(alias =>
                  currentJobAliasesSet.has(alias.alias)
                );

                if (!hasMatchingJobAlias) {
                  return false;
                }

                if (run.version !== job.versionInfo.runTargetNum) {
                  return false;
                }
              }

              return true;
            }) || [];

        const runOptions = runs.map((run: JobRun) => ({
          value: run.name,
          run,
          label: run.displayName,
        }));

        setRunOptions(runOptions);
      } catch (error) {
        console.error('Failed to fetch runs for project:', error);
        setRunOptions([]);
      } finally {
        setLoadingRuns(false);
      }
    },
    [job, selectedConfigSourceRun]
  );

  const onSelectRun = useCallback((selectedRun: RunOption | null) => {
    if (!selectedRun) {
      return;
    }
    setSelectedConfigSourceRun(selectedRun.run);
  }, []);

  // initialize project selector and fetch runs on mount
  useEffect(() => {
    if (!selectedProject && projectOptions.length > 0) {
      if (selectedConfigSourceRun) {
        const key = `${selectedConfigSourceRun.entityName}/${selectedConfigSourceRun.projectName}`;
        const projectOption = projectOptions.find(
          option => option.value === key
        );
        if (projectOption) {
          setSelectedProject(projectOption);
          // fetch runs to populate dropdown even when we already have a default run
          if (runOptions.length === 0) {
            onSelectProject(projectOption);
          }
        }
      } else if (entityName && projectName) {
        const currentProjectKey = `${entityName}/${projectName}`;
        const currentProjectOption = projectOptions.find(
          option => option.value === currentProjectKey
        );
        if (currentProjectOption) {
          setSelectedProject(currentProjectOption);
          onSelectProject(currentProjectOption);
        }
      }
    }
  }, [
    selectedProject,
    selectedConfigSourceRun,
    projectOptions,
    entityName,
    projectName,
    onSelectProject,
    runOptions.length,
  ]);

  return {
    projectOptions,
    runOptions,
    selectedProjectOption,
    selectedRunOption,
    selectedConfigSourceRun,
    loadingRuns,
    onSelectProject,
    onSelectRun,
    loadProjectOptions,
  };
};
